import { clientEnvModule } from "@/modules/client-env";

export type MemAgentHoldToTalkKey =
  | "tab"
  | "caps-lock"
  | "right-shift"
  | "right-alt"
  | "right-ctrl"
  | "scroll-lock";
export type MemAgentHoldToTalkKeySetting = MemAgentHoldToTalkKey | null;

export const MACOS_DEFAULT_MEM_AGENT_HOLD_TO_TALK_KEY: MemAgentHoldToTalkKey = "right-alt";
export const WINDOWS_DEFAULT_MEM_AGENT_HOLD_TO_TALK_KEY: MemAgentHoldToTalkKey = "right-ctrl";
export const DEFAULT_MEM_AGENT_HOLD_TO_TALK_KEY = MACOS_DEFAULT_MEM_AGENT_HOLD_TO_TALK_KEY;

export const MEM_AGENT_HOLD_TO_TALK_KEYS: readonly MemAgentHoldToTalkKey[] = [
  "tab",
  "caps-lock",
  "right-shift",
  "right-alt",
  "right-ctrl",
  "scroll-lock",
];

export const isMemAgentHoldToTalkKey = (value: unknown): value is MemAgentHoldToTalkKey =>
  typeof value === "string" && MEM_AGENT_HOLD_TO_TALK_KEYS.includes(value as MemAgentHoldToTalkKey);

/** Returns the platform fallback used before a user saves a hold-to-talk key. */
export const getDefaultMemAgentHoldToTalkKey = ({
  isWindows = clientEnvModule.isWindows(),
}: { isWindows?: boolean } = {}): MemAgentHoldToTalkKey =>
  isWindows ? WINDOWS_DEFAULT_MEM_AGENT_HOLD_TO_TALK_KEY : MACOS_DEFAULT_MEM_AGENT_HOLD_TO_TALK_KEY;

export const normalizeMemAgentHoldToTalkKey = (
  value: unknown,
  options?: { isWindows?: boolean }
): MemAgentHoldToTalkKeySetting => {
  if (isMemAgentHoldToTalkKey(value)) {
    return value;
  }

  if (value === "") {
    return null;
  }

  return getDefaultMemAgentHoldToTalkKey(options);
};

/**
 * `sentencePrefix` is the lowercase word that reads before the key glyph in a
 * "Press and hold {prefix} {tooltipKey} to talk" hint — "right" for the
 * right-side keys, empty otherwise.
 */
export const getMemAgentHoldToTalkKeyDisplay = (
  key: MemAgentHoldToTalkKey,
  { isWindows = clientEnvModule.isWindows() }: { isWindows?: boolean } = {}
): { label: string; accessibleLabel: string; tooltipKey: string; sentencePrefix: string } => {
  switch (key) {
    case "tab":
      return { label: "Tab", accessibleLabel: "Tab", tooltipKey: "⇥", sentencePrefix: "" };
    case "caps-lock":
      return {
        label: "Caps Lock",
        accessibleLabel: "Caps Lock",
        tooltipKey: "⇪",
        sentencePrefix: "",
      };
    case "right-shift":
      return {
        label: "Right Shift",
        accessibleLabel: "Right Shift",
        tooltipKey: "⇧",
        sentencePrefix: "right",
      };
    case "right-alt":
      return isWindows
        ? {
            label: "Right Alt",
            accessibleLabel: "Right Alt",
            tooltipKey: "Alt",
            sentencePrefix: "right",
          }
        : {
            label: "Right Option",
            accessibleLabel: "Right Option",
            tooltipKey: "⌥",
            sentencePrefix: "right",
          };
    case "right-ctrl":
      return isWindows
        ? {
            label: "Right Ctrl",
            accessibleLabel: "Right Ctrl",
            tooltipKey: "Ctrl",
            sentencePrefix: "right",
          }
        : {
            label: "Right Control",
            accessibleLabel: "Right Control",
            tooltipKey: "⌃",
            sentencePrefix: "right",
          };
    case "scroll-lock":
      return {
        label: "Scroll Lock",
        accessibleLabel: "Scroll Lock",
        tooltipKey: "Scroll",
        sentencePrefix: "",
      };
  }
};
